import { z } from 'zod';

/**
 * The blind-interview chat (chat spec §5, §9, §10).
 *
 * Two people, one room, two opaque links. This file holds everything that
 * crosses the wire, and nothing else: the room identifier and the two tokens
 * are deliberately ABSENT from the participant views below, because §5 keeps
 * `roomId` on the server at the same régime `sessionId` already follows - the
 * browser carries a token and learns nothing it could enumerate from.
 *
 * The tables these shapes project are `chat_rooms`, `chat_room_participants`
 * and `chat_messages`, and they sit OUTSIDE `sessions` and `turns` on purpose
 * (§9): `turns` is PAIRED - one row carries `agentText` and `personText` - and
 * a human conversation is not, while `sessions` demands a `configId` and a
 * `provenance` an interview has nothing to fill. A fabricated provenance would
 * put a phantom row in the corpus `cycle/corpus.ts` reads and contaminate the
 * cycle's population in silence.
 */

/**
 * Which side of the room a token names. Never in the URL (§5): the token
 * carries the role and the server resolves it.
 *
 * The role sits beside the consent as well as beside the message, and §6 says
 * why: a future count of « how many people consented » that ignored the role
 * would take an operator's acknowledgement for a subject's consent.
 */
export const chatRoleSchema = z.enum(['interviewer', 'guest']);
export type ChatRole = z.infer<typeof chatRoleSchema>;

/**
 * The four states of a room (§4, §9).
 *
 * `open` is the lobby, `chatting` is past the one-way door of §4.5, `closed`
 * is the interviewer's soft closure (§11) and `abandoned` records the guest who
 * left - a finding about the conduct, and exactly what the cycle wants to read.
 */
export const chatRoomStatusSchema = z.enum(['open', 'chatting', 'closed', 'abandoned']);
export type ChatRoomStatus = z.infer<typeof chatRoomStatusSchema>;

/**
 * The alphabet of a room reference, 32 characters with no visual ambiguity.
 *
 * `I` and `O` are out because they read as `1` and `0`; `0` and `1` are out for
 * the same reason read the other way. What is left is exactly 32 symbols, which
 * is the property the generator leans on: a byte maps onto it with `& 31` and no
 * modulo bias to argue about.
 *
 * It matters because this string is read aloud and retyped. §10 hands it to the
 * guest so she can ask for an erasure, and §12 resolves that request by
 * searching for it - a reference somebody mistypes is a request nobody can
 * honour.
 */
export const CHAT_REFERENCE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/** `7K4M-92XB`: two groups of four, built from the alphabet so the two cannot drift. */
export const CHAT_REFERENCE_PATTERN = new RegExp(
  `^[${CHAT_REFERENCE_ALPHABET}]{4}-[${CHAT_REFERENCE_ALPHABET}]{4}$`,
);

export const chatRoomReferenceSchema = z.string().regex(CHAT_REFERENCE_PATTERN);

/**
 * How long the two links resolve, counted FROM THE CREATION of the room (§10)
 * and not from the last message - decided, and worth stating because the other
 * reading is the intuitive one.
 *
 * Shared rather than backend-only because the lobby says the number out loud.
 * Note what this clock does NOT do: it never erases. The transcript is kept -
 * it is the deliverable of the cycle - and only the ENTRY expires.
 */
export const CHAT_LINK_TTL_HOURS = 24;

/**
 * How long a room that WAS held stays reachable after its closure.
 *
 * It exists because the entry window and the interview do not have to line up.
 * A room struck Monday 09:00 and held Tuesday 08:30 crosses its 24 hours DURING
 * the conversation, which §4 explicitly allows - and the moment the interviewer
 * closes it, the status leaves `chatting` and the creation window bites. Both
 * sides would then get S8 instead of S6, and the guest would never receive the
 * room reference §10 makes an obligation of the consent: the end screen is
 * where she is told the transcript is kept and given what to quote to ask for
 * an erasure.
 *
 * It only ever EXTENDS reachability - the guard takes the later of the two
 * deadlines - so « 24 hours from the creation » still governs every room that
 * was never held, which is the decision §10 recorded and this does not touch.
 */
export const CHAT_CLOSED_GRACE_HOURS = 1;

/**
 * How long a lobby poll counts as « present » (§4.3, §7).
 *
 * The lobby polls at ~1500 ms, so this tolerates six missed polls before the
 * other side reads « déconnecté ». Deliberately MUCH shorter than the socket's
 * `CHAT_HEARTBEAT.deadAfterMs` of 60 s: that number is pinned to nginx's
 * `proxy_read_timeout` and to the sixty seconds of silence a hard answer
 * legitimately takes, whereas nothing is being composed in a lobby - the only
 * question there is who is in the room, and a poll is cheap enough to answer it
 * in seconds.
 *
 * Shared rather than backend-only for the reason `CHAT_LINK_TTL_HOURS` is: the
 * lobby SAYS this out loud, so the screen and the machine have to read one
 * number. Two spellings would let a badge say « connecté » next to a machine
 * that had given the same person up.
 */
export const CHAT_LOBBY_PRESENCE_WINDOW_MS = 10_000;

/**
 * The participant as SHE sees herself, and the three facts a lobby is built on
 * (§4).
 *
 * `readyAt` is persisted on the room and not held on the socket, because being
 * ready is a fact about the person and has to survive a refresh. `lastSeenAt`
 * is what lets the interviewer tell « gone » from « composing a difficult
 * answer » (§11), which is the whole reason the guest needs no exit control.
 */
export const chatSelfParticipantSchema = z
  .object({
    role: chatRoleSchema,
    consentedAt: z.string().datetime().nullable(),
    readyAt: z.string().datetime().nullable(),
    lastSeenAt: z.string().datetime().nullable(),
  })
  .strict();
export type ChatSelfParticipant = z.infer<typeof chatSelfParticipantSchema>;

/**
 * The participant on the OTHER side - and it carries no `consentedAt`, which
 * is the whole point of it being a second shape.
 *
 * §6 requires a refusal to consent to be INVISIBLE to the other side: the one
 * who did not tick and the one who never arrived must both read « en attente ».
 * Served whole, the pair `(consentedAt: null, lastSeenAt: set)` says « arrived,
 * did not tick » and `(null, null)` says « never came » - the very distinction
 * §6 puts on the wrong side of the line, and it would be on the wire whether or
 * not a screen ever rendered it.
 *
 * `readyAt` is enough for the lobby to run on: the ready button is dark until
 * the box is ticked (§4.2), so being ready IMPLIES having consented and there
 * is nothing the peer's `consentedAt` would add. `lastSeenAt` stays, because
 * §11 needs it.
 *
 * `.strict()` is what makes this a guard rather than a comment: a projection
 * that started handing the peer's consent over fails the parse.
 */
export const chatPeerParticipantSchema = z
  .object({
    role: chatRoleSchema,
    readyAt: z.string().datetime().nullable(),
    lastSeenAt: z.string().datetime().nullable(),
  })
  .strict();
export type ChatPeerParticipant = z.infer<typeof chatPeerParticipantSchema>;

/**
 * One message, in the order it ARRIVED.
 *
 * Two stamps, and the server's order wins (§8): `composedAt` is when the person
 * validated, `seq` and `receivedAt` are when it landed. Rendering a message at
 * its place of WRITING would show a conversation nobody lived, and a re-read
 * would draw conclusions about a sequence that never happened. `composedAt`
 * stays on the wire because the gap is itself the information - a message from
 * the queue that arrives after the closure carries it.
 */
export const chatMessageSchema = z
  .object({
    /** Monotone within the room, starting at 1. The order of arrival, and the cursor. */
    seq: z.number().int().positive(),
    role: chatRoleSchema,
    /** The idempotency key the client minted, unique with the room (§8.1). */
    clientMessageId: z.string().min(1),
    text: z.string().min(1),
    composedAt: z.string().datetime(),
    receivedAt: z.string().datetime(),
  })
  .strict();
export type ChatMessage = z.infer<typeof chatMessageSchema>;

/**
 * What a posted message carries, and all it carries.
 *
 * No role: the token resolved it server-side, and a role in the body would be
 * a role the caller gets to choose. No `seq` either - the server numbers.
 *
 * `clientMessageId` is a uuid rather than a free string, because the client-side
 * queue of §8 is what mints it: a caller who reuses a value silently drops his
 * own message, and a uuid makes that a bug in one place instead of a convention
 * spread over four screens.
 */
export const chatMessageInputSchema = z
  .object({
    clientMessageId: z.string().uuid(),
    text: z.string().min(1),
    composedAt: z.string().datetime(),
  })
  .strict();
export type ChatMessageInput = z.infer<typeof chatMessageInputSchema>;

/**
 * The room as a PARTICIPANT sees it, once her token resolved.
 *
 * No `id`, no token, not even the other side's: §5 keeps the identifier on the
 * server, and the two links are indistinguishable to the eye precisely so that
 * one pasted into the wrong window does not give the blind protocol away.
 *
 * `expiresAt` is DERIVED at the read (§10) and never stored. Null means the
 * stored stamp could not be parsed, the soft-fail direction
 * `enrollment/experiences/availability.ts` already chose: at worst a room stays
 * open a little too long, whereas a broken clock costs a whole interview.
 */
export const chatRoomViewSchema = z
  .object({
    reference: chatRoomReferenceSchema,
    /** The role the token named - the one thing the browser is told about itself. */
    role: chatRoleSchema,
    status: chatRoomStatusSchema,
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    /** First message, so « jamais ouverte » is a displayed state and not a blank (§12). */
    openedAt: z.string().datetime().nullable(),
    closedAt: z.string().datetime().nullable(),
    /** The fingerprint of the consent text served, never a hand-kept version number (§6). */
    consentTextVersion: z.string().nullable(),
    /** This side, whole. */
    self: chatSelfParticipantSchema,
    /**
     * The other side, WITHOUT its consent (§6).
     *
     * Two named fields rather than an array of two, and the shape does the work
     * a comment used to: presence names the MISSING ROLE and never a person
     * (§4.3), so what a screen needs is « me » and « the other one », not a
     * list it has to search itself - and a list could not have carried two
     * different shapes without a reader guessing which it got.
     */
    peer: chatPeerParticipantSchema,
  })
  .strict();
export type ChatRoomView = z.infer<typeof chatRoomViewSchema>;

/**
 * The consent block a lobby serves ONE role, read from base (§6).
 *
 * A shape of its own rather than three more fields on `chatRoomViewSchema`, and
 * the reason is the polling: the room is re-asked every ~1500 ms while the
 * consent text moves a handful of times a year. Fetched once beside the room,
 * it also keeps the conversation and the end screens from carrying a paragraph
 * they never render.
 *
 * `notice` is the wording IN FORCE on the guest's side - the régime of
 * BUILD-829 reused and not rewritten, so a rewording reaches the next guest
 * instead of the next deploy of the bundle. `lead` and `acceptance` are the
 * room's own prose, and they differ by role: the guest is a subject giving a
 * consent, the interviewer is an operator acknowledging one, and §6 makes that
 * distinction load-bearing rather than cosmetic.
 *
 * It carries NO fingerprint. What the room records is computed server-side from
 * the base at the moment somebody accepts (§6: « jamais un numéro de version à
 * la main »), so a value travelling out and back would be a value a caller
 * could choose.
 */
export const chatConsentViewSchema = z
  .object({
    /** The room's own sentence for this role - what is true HERE and nowhere else. */
    lead: z.string().min(1),
    /** The paragraph that follows it. On the guest's side, the text in force in base. */
    notice: z.string().min(1),
    /** The label of the box, and the whole of what ticking it accepts. */
    acceptance: z.string().min(1),
  })
  .strict();
export type ChatConsentView = z.infer<typeof chatConsentViewSchema>;

/** The REST read of the history, which stays the source of truth beside the socket (§7). */
export const chatTranscriptSchema = z.object({ messages: z.array(chatMessageSchema) }).strict();
export type ChatTranscript = z.infer<typeof chatTranscriptSchema>;

/**
 * What creating a room hands back, ONCE.
 *
 * The two links are shown at the creation and nowhere else (§12): they are a
 * shared secret, and a screen that could redisplay them would be a second place
 * to leak them from. This is therefore the only shape in this file that carries
 * a token or the room identifier.
 */
export const chatRoomCredentialsSchema = z
  .object({
    id: z.string().min(1),
    reference: chatRoomReferenceSchema,
    interviewerToken: z.string().min(1),
    guestToken: z.string().min(1),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
  })
  .strict();
export type ChatRoomCredentials = z.infer<typeof chatRoomCredentialsSchema>;
